// Text-effect definitions — the SINGLE source of truth for the Style tab's
// effect tiles, their option cards, the Quick Style chips AND the engine's
// applyTextEffect(). Preview and canvas can never drift because both read the
// same defs.
//
// Slot model: fabric gives one `shadow`, one stroke set, one
// `textBackgroundColor` and one `path` per object — mutual exclusivity in
// other editors is a UI artifact, not a rendering limit. We expose two
// stackable slots here (shadow-slot + stroke-slot); Highlight and Curve are
// separate panel sections on their own props. Sticker claims BOTH slots
// atomically.
//
// derive(params, fontSize, ctx) returns a plain fabric prop patch. Every
// dimension is fontSize-relative (u = fontSize/100, the art-text convention)
// so effects stay proportional when text is resized. ctx = { fillColor }
// feeds smart defaults (echo inherits the fill; outline auto-contrasts).
// All Shadow construction uses ?? semantics — zero offsets are meaningful
// (glow) and must never coerce to legacy defaults.
import { Gradient, Shadow } from 'fabric';
// Universal-effects U1: the slot bag now dual-reads (data.effects on shapes/
// icons, data.textEffects on text — the read-compat alias) and the three
// physical-fact gates below are kind-aware. capabilityMatrix is pure data +
// pure functions (no fabric), so this import adds nothing to any chunk.
import { effectsBagOf, objectKindOf } from '../../lib/fx/capabilityMatrix';

const rad = (deg) => (deg * Math.PI) / 180;

/** Linear-gradient coords for an angle, in `percentage` units (0..1 of the
 * object's own box) so the ramp rides a resize for free — the fontSize-relative
 * law applies to paint too, not just to dimensions. 0° = left→right. */
function angleCoords(deg) {
  const a = rad(deg ?? 0);
  const dx = Math.cos(a) / 2;
  const dy = Math.sin(a) / 2;
  return { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy };
}

/** A 2-stop linear Gradient built SYNCHRONOUSLY — no loading, no await, so it
 * is legal inside a derive (unlike a texture Pattern, which needs an image and
 * therefore an engine-side async resolve; see the outline def's notes). */
function linearGradient(from, to, angle) {
  return new Gradient({
    type: 'linear',
    gradientUnits: 'percentage',
    coords: angleCoords(angle),
    colorStops: [
      { offset: 0, color: from },
      { offset: 1, color: to },
    ],
  });
}

/** Gradient FillValue → fabric Gradient, SYNCHRONOUSLY. Mirrors posterTools
 * resolveFill's gradient cases exactly (percentage units; the linear coords
 * `{x1: 1-x2, ...}` there are algebraically identical to angleCoords here) —
 * we cannot CALL resolveFill because it is async and derive is not. A
 * degenerate <2-stop ramp collapses to its first color rather than emitting a
 * Gradient fabric would paint as nothing. */
function gradientFromFillValue(fv) {
  const stops = (fv.stops || []).map((s) => ({ offset: s.offset, color: s.color }));
  if (stops.length < 2) return stops[0]?.color ?? '#ffffff';
  if (fv.type === 'radial-gradient') {
    return new Gradient({
      type: 'radial',
      gradientUnits: 'percentage',
      coords: { x1: 0.5, y1: 0.5, r1: 0, x2: 0.5, y2: 0.5, r2: 0.5 },
      colorStops: stops,
    });
  }
  return new Gradient({
    type: 'linear',
    gradientUnits: 'percentage',
    coords: angleCoords(fv.angle),
    colorStops: stops,
  });
}

/**
 * The SYNC half of resolving a `type:'fill'` param into stroke paint — the
 * contract behind the `strokeFillParam` def marker.
 *
 * A FillValue is `string | {solid} | {linear-gradient} | {radial-gradient} |
 * {texture}`. A plain string IS a valid FillValue, which is what keeps every
 * shipped preset and saved recipe (`borderColor: '#ffffff'`) working untouched.
 *
 *   string / {type:'solid'}  → the color string. Byte-identical to the
 *                              pre-'fill' contract.
 *   {type:'*-gradient'}      → a fabric Gradient. Pure construction, no I/O.
 *   {type:'texture'}         → **undefined**, meaning "OMIT the stroke key".
 *
 * That undefined is LOAD-BEARING, not a failure signal. A texture needs an
 * image decode (resolveFill → util.loadImage), which derive — pure and
 * synchronous — cannot await. The engine resolves it after derive and sets
 * `stroke` itself. Crucially, refreshTextGeometryEffects re-runs derive on
 * EVERY resize and text edit: if derive returned a solid placeholder here, each
 * refresh would CLOBBER the engine's already-resolved Pattern and the border
 * would silently drop back to a flat color mid-drag. Omitting the key leaves
 * the live Pattern untouched while strokeWidth still re-derives proportionally.
 */
export function strokePaintFromFillValue(fv) {
  if (fv == null) return undefined;
  if (typeof fv === 'string') return fv;
  if (fv.type === 'solid') return fv.color ?? '#ffffff';
  if (fv.type === 'linear-gradient' || fv.type === 'radial-gradient') return gradientFromFillValue(fv);
  if (fv.type === 'texture') return undefined; // engine resolves; see above
  return undefined;
}

/** {r,g,b} 0-255 from a color STRING — hex 3/6/8-digit or rgb()/rgba().
 * PaintPicker v2's paint-opacity law means live fills can be 'rgba()'
 * strings now; every derivation below must read them, alpha aside. */
function rgbTripleOf(color) {
  const s = String(color || '#000000').trim();
  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (m) {
    const c = (v) => Math.max(0, Math.min(255, Math.round(parseFloat(v))));
    return [c(m[1]), c(m[2]), c(m[3])];
  }
  let h = s.replace('#', '');
  if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h.padEnd(6, '0').slice(0, 6), 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgba(hex, alpha) {
  const [r, g, b] = rgbTripleOf(hex);
  const a = Math.max(0, Math.min(1, alpha ?? 1));
  return `rgba(${r}, ${g}, ${b}, ${Math.round(a * 100) / 100})`;
}

/** Relative luminance 0..1 of a color string (for auto-contrast defaults). */
export function luminance(hex) {
  const [r, g, b] = rgbTripleOf(hex);
  return 0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);
}

/** Best solid color hiding inside a live fabric fill (string | Gradient | Pattern). */
export function solidFromFabricFill(fill) {
  if (typeof fill === 'string') return fill;
  if (fill?.colorStops?.length) return fill.colorStops[0].color;
  return '#000000';
}

/** Clearing a slot restores these props. Removing hollow ALSO restores the
 * fill from data.textFill — the engine owns that half.
 * strokeDashArray is reset here because Trace is the one effect that sets it:
 * without this, removing Trace (or swapping it for Outline/Sticker) leaves the
 * dashes behind and the next stroke effect renders dotted. null is fabric's
 * own default, so this is a no-op for every other effect. */
export const SLOT_RESET = {
  shadow: { shadow: null },
  stroke: { stroke: null, strokeWidth: 0, paintFirst: 'fill', strokeDashArray: null },
};

/** Tile order in the Style grid (a virtual "None" tile renders first).
 * Trace sits next to Hollow — they are the two see-through-letter effects. */
export const EFFECT_ORDER = ['shadow', 'lift', 'glow', 'echo', 'outline', 'hollow', 'trace', 'sticker'];

export const EFFECT_DEFS = {
  shadow: {
    id: 'shadow',
    label: 'Shadow',
    slots: ['shadow'],
    params: [
      { key: 'distance', label: 'Distance', type: 'slider', min: 0, max: 100, step: 1, default: 40 },
      { key: 'direction', label: 'Direction', type: 'angle', min: -180, max: 180, step: 5, default: 45 },
      { key: 'softness', label: 'Softness', type: 'slider', min: 0, max: 100, step: 1, default: 30 },
      { key: 'strength', label: 'Strength', type: 'slider', min: 0, max: 100, step: 1, default: 60 },
      { key: 'color', label: 'Color', type: 'color', default: '#000000' },
    ],
    presets: [
      { label: 'Subtle', params: { distance: 20, direction: 45, softness: 50, strength: 40 } },
      { label: 'Drop', params: { distance: 40, direction: 45, softness: 25, strength: 60 } },
      { label: 'Floating', params: { distance: 30, direction: 90, softness: 70, strength: 45 } },
      { label: 'Long', params: { distance: 90, direction: 45, softness: 0, strength: 35 } },
    ],
    derive: (p, fontSize) => {
      const u = (fontSize || 40) / 100;
      return {
        shadow: new Shadow({
          color: rgba(p.color, (p.strength ?? 60) / 100),
          blur: (p.softness ?? 30) * 0.5 * u,
          offsetX: (p.distance ?? 40) * 0.5 * u * Math.cos(rad(p.direction ?? 45)),
          offsetY: (p.distance ?? 40) * 0.5 * u * Math.sin(rad(p.direction ?? 45)),
        }),
      };
    },
    tilePreview: () => ({ textShadow: '3px 3px 3px rgba(0,0,0,0.55)' }),
  },

  lift: {
    id: 'lift',
    label: 'Lift',
    slots: ['shadow'],
    params: [{ key: 'amount', label: 'Amount', type: 'slider', min: 0, max: 100, step: 1, default: 50 }],
    derive: (p, fontSize) => {
      const u = (fontSize || 40) / 100;
      const a = p.amount ?? 50;
      return {
        shadow: new Shadow({
          color: rgba('#000000', 0.2 + a * 0.004),
          blur: (4 + a * 0.55) * u,
          offsetX: 0,
          offsetY: a * 0.12 * u,
        }),
      };
    },
    tilePreview: () => ({ textShadow: '0 4px 10px rgba(0,0,0,0.5)' }),
  },

  glow: {
    id: 'glow',
    label: 'Glow',
    slots: ['shadow'],
    params: [
      { key: 'size', label: 'Size', type: 'slider', min: 0, max: 100, step: 1, default: 45 },
      { key: 'strength', label: 'Strength', type: 'slider', min: 0, max: 100, step: 1, default: 80 },
      { key: 'color', label: 'Color', type: 'color', default: '#fde047' },
    ],
    derive: (p, fontSize) => {
      const u = (fontSize || 40) / 100;
      // Zero offsets are the whole point — must ride the ??-safe path only.
      return {
        shadow: new Shadow({
          color: rgba(p.color, (p.strength ?? 80) / 100),
          blur: (p.size ?? 45) * 0.6 * u,
          offsetX: 0,
          offsetY: 0,
        }),
      };
    },
    tilePreview: () => ({ textShadow: '0 0 10px #fde047, 0 0 4px #fde047' }),
  },

  echo: {
    id: 'echo',
    label: 'Echo',
    slots: ['shadow'],
    params: [
      { key: 'distance', label: 'Distance', type: 'slider', min: 0, max: 100, step: 1, default: 40 },
      { key: 'direction', label: 'Direction', type: 'angle', min: -180, max: 180, step: 5, default: -45 },
      // null = inherit the text color at apply time (ctx.fillColor)
      { key: 'color', label: 'Color', type: 'color', default: null },
    ],
    derive: (p, fontSize, ctx) => {
      const u = (fontSize || 40) / 100;
      const d = p.distance ?? 40;
      // blur 0 on filled text renders a SOLID offset copy — a true native echo
      return {
        shadow: new Shadow({
          color: rgba(p.color ?? ctx?.fillColor ?? '#000000', 0.55),
          blur: 0,
          offsetX: d * 0.5 * u * Math.cos(rad(p.direction ?? -45)),
          offsetY: d * 0.5 * u * Math.sin(rad(p.direction ?? -45)),
        }),
      };
    },
    tilePreview: () => ({ textShadow: '-4px 4px 0 rgba(148,163,184,0.8)' }),
  },

  outline: {
    id: 'outline',
    label: 'Outline',
    slots: ['stroke'],
    // Rich outlines, and why only HALF of them live here:
    // fabric paints a stroke with any filler the fill accepts — Hollow already
    // proves it by routing data.textFill onto the stroke. But that routing is
    // an ENGINE move: it can await the texture image load. derive() is pure and
    // SYNCHRONOUS, so it can only build paint that needs no I/O.
    //   • Gradient  → `new Gradient({...})` is pure construction. Done here.
    //   • Texture   → needs resolveFill() → an <img> decode → async.
    //
    // The engine-side `strokeFill` path this def once asked for now EXISTS
    // (see sticker's `strokeFillParam`), and outline still does NOT adopt it —
    // a deliberate call, not an oversight. Outline's paint is already governed
    // by the `strokeStyle` segmented control, under which `color` does DOUBLE
    // DUTY as gradient stop 1. Widening `color` to type 'fill' would leave two
    // controls claiming authority over one output: with strokeStyle:'gradient'
    // and a texture in `color`, stop 1 is an object and the ramp is nonsense —
    // and "Outline style: Gradient" + "Color: <a texture>" is exactly the
    // confusing third mode we were told to avoid. Sticker has no such conflict:
    // borderColor is its border's sole paint authority.
    // The honest fix for outline is to RETIRE strokeStyle/strokeColor2/
    // strokeAngle in favor of one 'fill' param — a real migration of saved
    // recipes, not a widening — so it stays a separate lane.
    params: [
      { key: 'thickness', label: 'Thickness', type: 'slider', min: 1, max: 100, step: 1, default: 30 },
      // null = auto-contrast against the fill at apply time
      { key: 'color', label: 'Color', type: 'color', default: null },
      {
        key: 'strokeStyle', label: 'Outline style', type: 'segmented', default: 'solid',
        options: [
          { value: 'solid', label: 'Solid' },
          { value: 'gradient', label: 'Gradient' },
        ],
      },
      // Only meaningful once the outline is a gradient — `color` is stop 1.
      {
        key: 'strokeColor2', label: 'Fade to', type: 'color', default: '#38bdf8',
        showIf: (p) => p.strokeStyle === 'gradient',
      },
      {
        key: 'strokeAngle', label: 'Fade direction', type: 'angle', min: -180, max: 180, step: 5, default: 0,
        showIf: (p) => p.strokeStyle === 'gradient',
      },
      // Multi-ring outlines (Depth Everywhere N3): concentric echo bands
      // OUTSIDE the outline — the retro stacked-sticker look. rings:1 (the
      // default) is byte-identical to the pre-lane outline: derive() below
      // never reads these keys (native props untouched — stock consumers
      // keep rendering ring 1 as the portable approximation), and every
      // render-side branch gates on rings > 1. The seam paints rings 2..4 on
      // exact offsets of the union silhouette (fxContourOutline, lazy geo;
      // unavailable geometry degrades to rings 1). Colors alternate between
      // this outline's own paint and `ringColor2` (die-cut sticker white by
      // default — see fxContourOutline's header for the default's honesty
      // note). On shapes/icons the same trio rides the contourBand step via
      // the engine mirror.
      { key: 'rings', label: 'Rings', type: 'slider', min: 1, max: 4, step: 1, default: 1 },
      {
        key: 'ringGap', label: 'Ring spacing', type: 'slider', min: 1, max: 100, step: 1, default: 25,
        showIf: (p) => (p.rings ?? 1) > 1,
      },
      {
        key: 'ringColor2', label: 'Second ring color', type: 'color', default: '#ffffff',
        showIf: (p) => (p.rings ?? 1) > 1,
      },
      {
        key: 'position', label: 'Position', type: 'segmented', default: 'outside', advanced: true,
        options: [
          { value: 'outside', label: 'Outside' },
          { value: 'centered', label: 'Centered' },
        ],
      },
    ],
    derive: (p, fontSize, ctx) => {
      const u = (fontSize || 40) / 100;
      const base = (p.thickness ?? 30) * 0.25 * u;
      const color = p.color ?? (luminance(ctx?.fillColor ?? '#000000') > 0.5 ? '#000000' : '#ffffff');
      // Outside doubles the width and paints stroke FIRST so the inner half
      // hides under the fill — glyphs never get eaten (the sticker trick).
      const outside = (p.position ?? 'outside') === 'outside';
      return {
        // 'solid' (the default) must stay byte-identical to the pre-gradient
        // contract — the geometry below never branches on strokeStyle.
        stroke: (p.strokeStyle ?? 'solid') === 'gradient'
          ? linearGradient(color, p.strokeColor2 ?? '#38bdf8', p.strokeAngle ?? 0)
          : color,
        strokeWidth: outside ? base * 2 : base,
        paintFirst: outside ? 'stroke' : 'fill',
        strokeUniform: true,
        strokeLineJoin: 'round',
        strokeLineCap: 'round',
      };
    },
    tilePreview: () => ({ WebkitTextStroke: '1.5px #94a3b8' }),
  },

  hollow: {
    id: 'hollow',
    label: 'Hollow',
    slots: ['stroke'],
    // The fill recipe (data.textFill) is routed to the STROKE while hollow is
    // active; the engine resolves it (gradients paint outlines natively) and
    // restores the fill when hollow is removed.
    fillRouting: 'stroke',
    // UNION-CONTOUR OUTLINE (owner 2026-07-20, the "internal lines in a/b/y"
    // screenshot): fonts compose glyphs from OVERLAPPING subpaths — a filled
    // render hides the overlaps, but a fill-less stroked render strokes the
    // RAW path, so construction seams show up as lines INSIDE the letters.
    // This marker asks the live render seam to stroke the glyph's TRUE UNION
    // silhouette instead (outer contour + genuine counters only — see
    // lib/fx/fxContourOutline.js). Engine/seam key off THIS MARKER, never off
    // the effect id (the effectDefs law). DEGRADE SPLIT: the stroke props this
    // def derives stay exactly as before — stock fabric consumers render the
    // raw-path stroke (seams and all) as the portable approximation; the seam
    // supersedes it visually (same split as the sticker ring vs its plate).
    contourOutline: true,
    params: [{ key: 'thickness', label: 'Thickness', type: 'slider', min: 1, max: 100, step: 1, default: 30 }],
    derive: (p, fontSize) => {
      const u = (fontSize || 40) / 100;
      return {
        fill: 'transparent',
        strokeWidth: (p.thickness ?? 30) * 0.2 * u,
        paintFirst: 'fill',
        strokeUniform: true,
        strokeLineJoin: 'round',
        strokeLineCap: 'round',
        // stroke paint itself comes from data.textFill via the engine
      };
    },
    tilePreview: () => ({ WebkitTextFillColor: 'transparent', WebkitTextStroke: '1.5px #f1f5f9' }),
  },

  trace: {
    id: 'trace',
    label: 'Trace letters',
    slots: ['stroke'],
    // NO fillRouting — deliberate, and the opposite call from Hollow.
    // fillRouting:'stroke' means "paint data.textFill onto the stroke": the
    // engine resolves the text's own fill recipe and OVERWRITES stroke with it
    // after derive runs. That is exactly wrong twice over for a tracing guide:
    //   1. A guide line wants a soft, low-ink gray that a pencil mark reads on
    //      top of. Inheriting the display fill would draw the guide in the
    //      headline's bold gradient or one of the 216 textures — unreadable to
    //      trace over, and a toner sink on a class set of 30.
    //   2. The routed stroke would clobber this def's own `color` param, so the
    //      Color control would silently do nothing.
    // Trace therefore owns its stroke paint outright and leaves data.textFill
    // alone.
    //
    // The cost of not routing, stated plainly: Trace still blanks the fill, but
    // the engine only calls restoreTextFill() when fillRouting === 'stroke'.
    // So removing Trace resets the stroke and leaves fill:'transparent' behind
    // — invisible text until the user picks a color. That is an ENGINE gap, not
    // a def gap: data.textFill is untouched and restoreTextFill() would put it
    // back correctly if it were called. `clearsFill` below is the marker for
    // that fix; it is inert until useCanvasEditor.js (another agent's file)
    // widens its three restore gates to:
    //     if (def.fillRouting === 'stroke' || def.clearsFill) await restoreTextFill(object)
    // in removeTextEffect, clearTextEffects and the displacement branch of
    // applyTextEffect (composeRecipePatch wants the mirror of it, but no stock
    // recipe ships Trace, so that half is not yet load-bearing).
    clearsFill: true,
    // Trace strokes fill-less glyphs too, so it shows the same raw-subpath
    // construction seams Hollow does — same union-contour law, same degrade
    // split (see the hollow def's note). The seam draws the union band with
    // this def's own dash law (strokeDashArray ×u) via ctx.setLineDash.
    contourOutline: true,
    params: [
      { key: 'thickness', label: 'Line thickness', type: 'slider', min: 1, max: 100, step: 1, default: 30 },
      { key: 'dash', label: 'Dash length', type: 'slider', min: 1, max: 100, step: 1, default: 30 },
      { key: 'gap', label: 'Gap between dashes', type: 'slider', min: 1, max: 100, step: 1, default: 25 },
      { key: 'color', label: 'Line color', type: 'color', default: '#94a3b8' },
      // Multi-ring outlines (Depth Everywhere N3): concentric dashed guide
      // bands outside the trace — same law as the outline def's trio (see
      // that block's note): rings:1 is byte-identical to the pre-lane trace
      // (derive untouched, render branches gate on rings > 1); the seam
      // paints rings 2..4 on exact offsets with the dashes riding EACH
      // ring's own path; colors alternate Line color / Second ring color.
      { key: 'rings', label: 'Rings', type: 'slider', min: 1, max: 4, step: 1, default: 1 },
      {
        key: 'ringGap', label: 'Ring spacing', type: 'slider', min: 1, max: 100, step: 1, default: 25,
        showIf: (p) => (p.rings ?? 1) > 1,
      },
      {
        key: 'ringColor2', label: 'Second ring color', type: 'color', default: '#ffffff',
        showIf: (p) => (p.rings ?? 1) > 1,
      },
    ],
    presets: [
      { label: 'Fine', params: { thickness: 16, dash: 18, gap: 14 } },
      { label: 'Classic', params: { thickness: 30, dash: 30, gap: 25 } },
      { label: 'Dotted', params: { thickness: 42, dash: 4, gap: 26 } },
      { label: 'Bold', params: { thickness: 62, dash: 48, gap: 30 } },
    ],
    derive: (p, fontSize) => {
      const u = (fontSize || 40) / 100;
      return {
        // Hollow glyphs: the whole point is an empty letter to write inside.
        // See `clearsFill` above for the removal-restore caveat.
        fill: 'transparent',
        stroke: p.color ?? '#94a3b8',
        strokeWidth: (p.thickness ?? 30) * 0.18 * u,
        // fontSize-relative like every other dimension: the dash rhythm has to
        // survive a corner-drag resize, or a 3-letter name and a 20-word
        // sentence would trace at different scales on the same sheet.
        strokeDashArray: [(p.dash ?? 30) * u, (p.gap ?? 25) * u],
        paintFirst: 'fill',
        strokeUniform: true,
        strokeLineCap: 'round',
      };
    },
    // CSS cannot dash a text stroke (-webkit-text-stroke has no dash control),
    // so the tile shows honest hollow letters in the guide gray and lets the
    // grid's existing "previews are approximate" disclaimer carry the rest.
    tilePreview: () => ({ WebkitTextFillColor: 'transparent', WebkitTextStroke: '1.25px #94a3b8' }),
  },

  sticker: {
    id: 'sticker',
    label: 'Sticker',
    slots: ['shadow', 'stroke'], // atomic: claims both
    // A sticker's die-cut border is its OWN paint — a foil edge around plain
    // black letters is the whole point — so it must NOT go through
    // fillRouting:'stroke' (that hijacks data.textFill, i.e. the TEXT's fill
    // recipe; that mechanism IS Hollow). Instead this marker names the param
    // holding the border's independent FillValue. The engine keys off the
    // MARKER, never off `id === 'sticker'`, so any future def can opt in by
    // declaring a 'fill' param and naming it here.
    strokeFillParam: 'borderColor',
    // PLATE MODEL (plate-model lane, owner 2026-07-20): a sticker is a SOLID
    // PLATE under the letters in EVERY mode — never a glyph-hugging ring. It
    // rides a data.fx filter step (this marker names its id); the engine
    // mirrors {shape, cornerRadius, padWidth, padHeight, beveled, finish,
    // border, fill:borderColor, edge} into data.fx via applyTextEffect (see
    // useCanvasEditor's syncShapeFxStep). The step exists whenever the sticker
    // is active (match-text is now a plate too, not the old no-op). DEGRADE
    // SPLIT: the derive below still emits stroke ring props purely as the
    // stock-fabric approximation (recipe = portable ring; live = plate) — the
    // seam suppresses that ring in the LIVE base render (fxRender: a
    // stickerShape step ⇒ base strokeWidth 0) so the plate is the sole live
    // paint. Saved pre-lane stickers migrate to plates (owner-ruled correct).
    shapeFxStep: 'stickerShape',
    params: [
      // Plate silhouette. 'outline' = "Match text" — the plate is the die-cut
      // glyph silhouette (a sticker shaped like the words); the four shape
      // modes draw an analytic badge. ALL are SOLID PLATES under the letters
      // (plate-model lane, owner 2026-07-20). Glyph labels — the titles carry
      // the accessible names (TbSegmented forwards them).
      {
        key: 'shape', label: 'Shape', type: 'segmented', default: 'outline',
        options: [
          { value: 'outline', label: 'Aa', title: 'Match text' },
          { value: 'circle', label: '●', title: 'Circle' },
          { value: 'rectangle', label: '▬', title: 'Rectangle' },
          { value: 'square', label: '■', title: 'Square' },
          { value: 'rounded', label: '▢', title: 'Rounded rectangle' },
        ],
      },
      // border = the MARGIN (owner 2026-07-20): the space between the text
      // edges and the plate edges, NOT a ring thickness. max 200; scales
      // linearly (0.3·u per point). One dial, every mode.
      { key: 'border', label: 'Border', type: 'slider', min: 1, max: 200, step: 1, default: 45 },
      // Corner roundness in recipe units (u = fontSize/100), rounded only.
      {
        key: 'cornerRadius', label: 'Corner radius', type: 'slider', min: 0, max: 60, step: 1, default: 18,
        showIf: (p) => p.shape === 'rounded',
      },
      // Wider / Taller — extra per-axis margin on top of Border (owner
      // 2026-07-20). Grows analytic badges per axis: square→rectangle,
      // circle→ellipse when the two differ. Meaningless for the glyph-hugging
      // Match-text plate (its margin is isotropic), so hidden there.
      {
        key: 'padWidth', label: 'Wider', type: 'slider', min: 0, max: 250, step: 1, default: 0,
        showIf: (p) => (p.shape ?? 'outline') !== 'outline',
      },
      {
        key: 'padHeight', label: 'Taller', type: 'slider', min: 0, max: 250, step: 1, default: 0,
        showIf: (p) => (p.shape ?? 'outline') !== 'outline',
      },
      // Domes the PLATE only (its die-cut edge) via the fx step's shading pass
      // — the letters are never touched by this control. The standard Bevel
      // step is the letters' dome; the two compose (layer separation).
      { key: 'beveled', label: 'Beveled sticker', type: 'toggle', default: false },
      // Finish — the plate dome's surface family (owner 2026-07-20), only
      // meaningful once the plate is beveled. satin = the default look; the
      // values are anchored to the baked engine's style bundles (fxBevel
      // FINISHES). Same chips as the standard bevel's Finish.
      {
        key: 'finish', label: 'Finish', type: 'segmented', default: 'satin',
        showIf: (p) => !!p.beveled,
        options: [
          { value: 'matte', label: 'Matte' },
          { value: 'satin', label: 'Satin' },
          { value: 'glossy', label: 'Glossy' },
          { value: 'metal', label: 'Metal' },
        ],
      },
      // type 'fill' (widened from 'color'), but the KEY and the '#ffffff'
      // default are deliberately unchanged: the 273-style manifest and every
      // saved recipe reference `borderColor`, and a plain string is already a
      // valid FillValue — so all of them stay valid with zero migration.
      // Shape modes paint the badge with this same value (solid / gradient /
      // manifest texture at the 2D pattern density).
      { key: 'borderColor', label: 'Border color', type: 'fill', default: '#ffffff' },
      // Plate edge contour (owner 2026-07-20: "if we want borders on the
      // sticker, we can use the border tool"). {color, width} | null — an
      // inside-aligned stroke following the plate's own die-cut silhouette,
      // driven by the Row-2 border tool when a sticker is active (its home;
      // hidden from this card via showIf so there are no twin controls). The
      // fx step renders it; null = no edge (byte-stable). The Row-2 routing +
      // 'Letter outlines live in Effects → Outline' guard on plain text is the
      // remaining ribbon coordination (see RETURN debt).
      {
        key: 'edge', label: 'Plate border', type: 'edge', default: null,
        showIf: () => false,
      },
      // ---- v3 (Depth-Everywhere N1, 2026-07-22) — engine-first params. The
      // fx step (fxStickerShape v3) renders all four TODAY via the fx param
      // grammar (patchTextFx('stickerShape', …)); they are declared here so
      // the recipe grammar, defaults and EDUCATOR WORDING are canon, but
      // stay hidden (the `edge` precedent) until the UI lane (a) adds the
      // keys to useCanvasEditor's syncShapeFxStep mirror — which currently
      // enumerates and would DROP them — and (b) un-hides these showIfs.
      // Edge: Smooth / Scalloped — the die-cut edge style.
      {
        key: 'edgeStyle', label: 'Edge', type: 'segmented', default: 'smooth',
        options: [
          { value: 'smooth', label: 'Smooth' },
          { value: 'scallop', label: 'Scalloped' },
        ],
      },
      // One scallop knob: bump size (drives disc radius + spacing together).
      {
        key: 'scallopSize', label: 'Scallop size', type: 'slider', min: 4, max: 100, step: 1, default: 50,
      },
      // Sticker body: Flat / Puffy — puffy is a real 3D slab; it REPLACES
      // the Beveled dome (the two are exclusive — puffy IS the 3D bevel).
      {
        key: 'body', label: 'Sticker body', type: 'segmented', default: 'flat',
        options: [
          { value: 'flat', label: 'Flat' },
          { value: 'puffy', label: 'Puffy' },
        ],
      },
      {
        key: 'puffDepth', label: 'Puffiness', type: 'slider', min: 1, max: 100, step: 1, default: 40,
      },
      // STUB — volumeMaterials preset id for the puffy body (a later lane
      // resolves it; accepted + stored now so recipes are forward-stable).
      {
        key: 'bodyMaterial', label: 'Body material', type: 'segmented', default: null,
        showIf: () => false, options: [],
      },
    ],
    derive: (p, fontSize) => {
      const u = (fontSize || 40) / 100;
      const shadow = new Shadow({
        color: 'rgba(0,0,0,0.35)',
        blur: 12 * u,
        offsetX: 0,
        offsetY: 6 * u,
      });
      // Shape modes: NO halo — the badge (fx step) is the border. The stroke
      // keys mirror SLOT_RESET.stroke so switching outline → circle clears
      // the halo in the same patch (same id = no displacement reset runs).
      // The drop shadow stays: fabric applies it around whatever _render
      // paints, so it hugs the badge silhouette once the step draws it.
      // diecutStrokeReach reads 0 here (no stroke) — analytic badges have no
      // pockets to close, so the die-cut lane skips by construction.
      if ((p.shape ?? 'outline') !== 'outline') {
        return {
          stroke: null,
          strokeWidth: 0,
          paintFirst: 'fill',
          strokeUniform: true,
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
          shadow,
        };
      }
      // Match-text (plate-model lane): these stroke ring props are now the
      // STOCK-FABRIC DEGRADE approximation only — the LIVE base render
      // suppresses this stroke (fxRender: a stickerShape step ⇒ base
      // strokeWidth 0) and the fx plate is the real paint. Priority (owner
      // PART 6, deferred wiring): when the Outline EFFECT stacks on a sticker
      // it OWNS the stroke slot (letter ink) and this ring approximation
      // yields — stock viewers then show the letters' outline. Three branches,
      // all inside strokePaintFromFillValue: solid → a color string, gradient
      // → a synchronously-built Gradient, texture → undefined (OMIT the stroke
      // key so the engine's async resolve owns it and derive never clobbers
      // the live Pattern on refresh).
      const paint = strokePaintFromFillValue(p.borderColor ?? '#ffffff');
      return {
        // Spread keeps `stroke` FIRST when present, preserving the exact prop
        // patch (keys and order) the solid path emitted before this change.
        ...(paint !== undefined ? { stroke: paint } : null),
        strokeWidth: Math.max(2, (p.border ?? 45) * 0.3 * u),
        paintFirst: 'stroke',
        strokeUniform: true,
        strokeLineJoin: 'round',
        strokeLineCap: 'round',
        shadow,
      };
    },
    tilePreview: () => ({
      WebkitTextStroke: '2px #ffffff',
      textShadow: '0 3px 6px rgba(0,0,0,0.5)',
    }),
  },
};

/** Merge user params over a def's defaults. */
export function effectDefaults(def) {
  const out = {};
  for (const p of def.params) out[p.key] = p.default;
  return out;
}

/**
 * Outward stroke spill (object units, strokeWidth/2) of a DIE-CUT BAND — the
 * family the raster die-cut closing applies to — or 0.
 *
 * A die-cut band is a stroke-slot effect that paints ONE continuous border
 * around an opaque body: sticker (the `strokeFillParam` marker — the border
 * owns its paint) and outline (the base stroke-slot case). Keyed off def
 * markers and the object's physical stroke facts, never off effect ids, so
 * any future band effect qualifies by construction.
 *
 * Deliberately DISQUALIFIED (each for a measured reason, 2026-07-16):
 *   - fillRouting:'stroke' (hollow): the body is see-through BY DESIGN, and
 *     its designed interior slit is sub-border-scale by construction
 *     (stem − SW: at thickness 60 a 5u slit vs a 3u closing radius) — die-cut
 *     closing would fill the very transparency the effect exists to create.
 *   - clearsFill (trace): a see-through tracing guide, and dashed — its gaps
 *     are the point.
 *   - a live strokeDashArray (the physical fact behind "dashed"): dash gaps
 *     must never bridge, whatever def they came from.
 */
export function diecutStrokeReach(obj) {
  // TEXT ONLY (universal-effects U1): the die-cut closing is the pocket-
  // above-the-i fix for GLYPH borders. Non-text kinds never paint a native
  // band from a slot effect — a shape's live stroke is the Border TOOL's
  // content (sticker on a shape carries strokeWidth 0), so reading the
  // physical stroke facts below would mis-attribute that content stroke to
  // the effect and close pockets that are real geometry.
  if (objectKindOf(obj) !== 'text') return 0;
  const entry = effectsBagOf(obj)?.stroke;
  const def = entry?.id ? EFFECT_DEFS[entry.id] : null;
  if (!def || !(def.slots || []).includes('stroke')) return 0;
  if (def.fillRouting === 'stroke' || def.clearsFill) return 0;
  if (Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length) return 0;
  if (!obj.stroke) return 0;
  const sw = Number(obj.strokeWidth) || 0;
  return sw > 0 ? sw / 2 : 0;
}

/**
 * LAYER-SEPARATION LAW (owner 2026-07-20): true when the object's raster is a
 * LETTERS layer plus a SEPARATE sticker layer (halo band or badge), so any fx
 * filter that shades "the text" must build its height/shading from a
 * glyph-only mask — the seam's renderVariant('noStroke') pass — and leave the
 * sticker layer flat (the sticker's own `beveled` param is that layer's only
 * dome). Gates the seam hook itself, so effects stay dumb: they just check
 * for env.renderVariant.
 *
 * Qualifies (same family logic as diecutStrokeReach, sharing its reasons):
 *   - a strokeFillParam def (sticker) — ALWAYS, stroke live or not: shape
 *     modes carry strokeWidth 0 yet the badge step still composites a second
 *     layer under the letters, and the variant render is what keeps the
 *     letters' mask honest wherever the badge lands in the chain;
 *   - any other stroke-slot band with live, undashed stroke ink (outline):
 *     the halo is a band around opaque glyphs — dome the glyphs, not the
 *     composite.
 * Disqualified: fillRouting:'stroke' (hollow) and clearsFill (trace) — there
 * the stroke IS the letterform; a stroke-suppressed pass would be an empty
 * raster and the bevel would vanish. Their single-layer behavior is the
 * feature, exactly as before this lane.
 *
 * CONTOUR-OUTLINE INTERPLAY (2026-07-20, union-contour lane): the exclusion
 * above STANDS even now that hollow/trace render as a union-contour band
 * (contourOutline marker). The contour render changes the band's GEOMETRY
 * SOURCE (union silhouette instead of the raw path), not the layer structure:
 * there is still exactly ONE layer of ink — the band — and it is the letters'
 * ink. The bevel must therefore keep its classic single-raster path and dome
 * the band's own alpha directly (the metallic domed-band look, now seam-free
 * because the base it shades is seam-free). Joining the mask family instead
 * would hand the bevel a stroke-suppressed variant that is EMPTY for these
 * effects — height field zero, bevel gone. The seam still exposes
 * env.renderVariant for the contour family, but only the 'filledSolid' kind
 * (the union-silhouette source); 'noStroke' returns undefined there and
 * fxBevel's existing `if (v && v.canvas)` guard already falls back to the
 * single-layer path — verified against fxBevel.js runBevel.
 */
export function glyphMaskWanted(obj) {
  const entry = effectsBagOf(obj)?.stroke;
  const def = entry?.id ? EFFECT_DEFS[entry.id] : null;
  if (!def || !(def.slots || []).includes('stroke')) return false;
  if (def.fillRouting === 'stroke' || def.clearsFill) return false;
  if (def.strokeFillParam) return true; // sticker family — halo OR badge mode
  // The band-with-live-ink tail is TEXT physics: a non-text object's live
  // stroke is the Border tool's content, never a slot effect's band
  // (universal-effects U1; icon Outline rides the contour fx step and needs
  // no mask — its band forms from the silhouette variant directly).
  if (objectKindOf(obj) !== 'text') return false;
  if (Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length) return false;
  return !!obj.stroke && (Number(obj.strokeWidth) || 0) > 0;
}

/**
 * UNION-CONTOUR LAW (owner 2026-07-20): true when the object's live render
 * should stroke the glyphs' TRUE UNION silhouette instead of fabric's raw
 * glyph path — the see-through-letter family (contourOutline def marker:
 * hollow, trace), whose fill-less stroked render otherwise shows the fonts'
 * internal construction seams (overlapping subpaths inside a/b/y/w…).
 *
 * Keyed off the DEF MARKER plus the physical stroke facts (a band needs live
 * stroke ink), never off effect ids — any future see-through stroke effect
 * qualifies by declaring the marker. Gates BOTH render paths: the fx seam's
 * base render (renderBaseOffscreen) and the plain no-fx lane
 * (fxRender.renderContourPlain, attached by useCanvasEditor's seam sync).
 * NOT gated on ownCaching or fx presence here — this is the pure recipe
 * question; the render sites own their own mode fences.
 */
export function contourOutlineWanted(obj) {
  // TEXT ONLY (universal-effects U1): this is the glyph construction-seam
  // fix — shape paths have no overlapping subpaths, and Hollow-on-shape is
  // pure native (its stock stroke render is already seam-free). A shape with
  // Hollow live DOES carry the contourOutline def + live stroke ink, so
  // without this gate the text band machinery would claim it.
  if (objectKindOf(obj) !== 'text') return false;
  const entry = effectsBagOf(obj)?.stroke;
  const def = entry?.id ? EFFECT_DEFS[entry.id] : null;
  if (!def || !def.contourOutline) return false;
  if (!(def.slots || []).includes('stroke')) return false;
  return !!obj.stroke && (Number(obj.strokeWidth) || 0) > 0;
}
